import { memo } from 'react';
import {
  ComposableMap,
  Geographies,
  Geography,
  ZoomableGroup
} from 'react-simple-maps';

interface WorldMapChartProps {
  countries: Record<string, number>;
  isLoading?: boolean;
}

// Natural Earth world topology (simplified)
const geoUrl = "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";

// ISO 3166-1 alpha-2 country code mapping
const countryCodeMap: Record<string, string> = {
  'US': 'United States',
  'CA': 'Canada',
  'GB': 'United Kingdom',
  'DE': 'Germany',
  'FR': 'France',
  'IT': 'Italy',
  'ES': 'Spain',
  'AU': 'Australia',
  'JP': 'Japan',
  'IN': 'India',
  'BR': 'Brazil',
  'MX': 'Mexico',
  'CN': 'China',
  'RU': 'Russia',
  'KR': 'South Korea',
  'NL': 'Netherlands',
  'SE': 'Sweden',
  'NO': 'Norway',
  'DK': 'Denmark',
  'FI': 'Finland',
  'IE': 'Ireland',
  'BE': 'Belgium',
  'CH': 'Switzerland',
  'AT': 'Austria',
  'PL': 'Poland',
  'CZ': 'Czech Republic',
  'HU': 'Hungary',
  'PT': 'Portugal',
  'GR': 'Greece',
  'TR': 'Turkey',
  'IL': 'Israel',
  'SA': 'Saudi Arabia',
  'AE': 'United Arab Emirates',
  'ZA': 'South Africa',
  'EG': 'Egypt',
  'NG': 'Nigeria',
  'KE': 'Kenya',
  'AR': 'Argentina',
  'CL': 'Chile',
  'CO': 'Colombia',
  'PE': 'Peru',
  'VE': 'Venezuela',
  'TH': 'Thailand',
  'VN': 'Vietnam',
  'ID': 'Indonesia',
  'MY': 'Malaysia',
  'PH': 'Philippines',
  'SG': 'Singapore',
  'NZ': 'New Zealand',
  'UA': 'Ukraine',
  'RO': 'Romania',
  'BG': 'Bulgaria',
  'HR': 'Croatia',
  'SK': 'Slovakia',
  'SI': 'Slovenia',
  'LT': 'Lithuania',
  'LV': 'Latvia',
  'EE': 'Estonia',
  'IS': 'Iceland',
  'MT': 'Malta',
  'CY': 'Cyprus',
  'LU': 'Luxembourg',
  'LI': 'Liechtenstein',
  'MC': 'Monaco',
  'SM': 'San Marino',
  'VA': 'Vatican City',
  'AD': 'Andorra'
};

// Reverse mapping for country names to codes
const reverseCountryCodeMap: Record<string, string> = {};
Object.entries(countryCodeMap).forEach(([code, name]) => {
  reverseCountryCodeMap[name] = code;
  // Add some common alternative names
  if (name === 'United States') reverseCountryCodeMap['USA'] = code;
  if (name === 'United Kingdom') reverseCountryCodeMap['UK'] = code;
});

export const WorldMapChart = memo(({ countries, isLoading }: WorldMapChartProps) => {
  if (isLoading) {
    return (
      <div className="bg-gray-50 rounded-lg p-4">
        <h4 className="text-sm font-semibold text-gray-700 mb-3">World Map</h4>
        <div className="h-96 bg-gray-100 rounded animate-pulse flex items-center justify-center">
          <div className="text-gray-400">Loading map...</div>
        </div>
      </div>
    );
  }

  // Country code to full name mapping (matching react-simple-maps geography names)
  const countryCodeToName: Record<string, string> = {
    'US': 'United States of America',
    'CA': 'Canada',
    'GB': 'United Kingdom',
    'DE': 'Germany',
    'FR': 'France',
    'JP': 'Japan',
    'AU': 'Australia',
    'BR': 'Brazil',
    'IN': 'India',
    'CN': 'China',
    'RU': 'Russia',
    'MX': 'Mexico',
    'IT': 'Italy',
    'ES': 'Spain',
    'KR': 'South Korea',
    'NL': 'Netherlands',
    'SE': 'Sweden',
    'NO': 'Norway',
    'FI': 'Finland',
    'DK': 'Denmark',
    'CH': 'Switzerland',
    'AT': 'Austria',
    'BE': 'Belgium',
    'PT': 'Portugal',
    'IE': 'Ireland',
    'PL': 'Poland',
    'CZ': 'Czech Republic',
    'HU': 'Hungary',
    'GR': 'Greece',
    'TR': 'Turkey',
    'IL': 'Israel',
    'AE': 'United Arab Emirates',
    'SA': 'Saudi Arabia',
    'EG': 'Egypt',
    'ZA': 'South Africa',
    'NG': 'Nigeria',
    'KE': 'Kenya',
    'TH': 'Thailand',
    'VN': 'Vietnam',
    'MY': 'Malaysia',
    'SG': 'Singapore',
    'ID': 'Indonesia',
    'PH': 'Philippines',
    'NZ': 'New Zealand',
    'AR': 'Argentina',
    'CL': 'Chile',
    'CO': 'Colombia',
    'PE': 'Peru',
    'VE': 'Venezuela'
  };

  // Prepare data for visualization
  const maxCount = Math.max(...Object.values(countries));
  const countryData: Record<string, { count: number; intensity: number }> = {};
  
  // Process country data and map to full country names
  Object.entries(countries).forEach(([countryName, count]) => {
    if (countryName === 'Unknown' || !countryName || count === 0) return;
    
    let fullCountryName = countryName;
    
    // Map country codes to full names for geography matching
    if (countryName.length === 2 && countryCodeToName[countryName]) {
      fullCountryName = countryCodeToName[countryName];
    }
    
    countryData[fullCountryName] = {
      count,
      intensity: maxCount > 0 ? count / maxCount : 0
    };
  });

  const getCountryColor = (countryId: string) => {
    const data = countryData[countryId];
    if (!data) return '#f3f4f6'; // Light gray for countries with no data
    
    const intensity = data.intensity;
    if (intensity === 0) return '#f3f4f6';
    
    // Blue color scale based on intensity
    const opacity = Math.max(0.2, intensity);
    return `rgba(59, 130, 246, ${opacity})`;
  };

  const getCountryStroke = (countryId: string) => {
    return countryData[countryId] ? '#1e40af' : '#d1d5db';
  };

  const handleCountryHover = (countryId: string) => {
    const data = countryData[countryId];
    if (data) {
      // Add hover effect logic here if needed
    }
  };

  const totalVotes = Object.values(countries).reduce((sum, count) => sum + count, 0);
  const countriesWithData = Object.keys(countryData).length;

  return (
    <div className="bg-gray-50 rounded-lg p-4">
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-sm font-semibold text-gray-700">Global Distribution</h4>
        <div className="text-xs text-gray-500">
          {countriesWithData} countries • {totalVotes} votes
        </div>
      </div>
      
      <div className="relative">
        <ComposableMap
          projection="geoNaturalEarth1"
          projectionConfig={{
            scale: 140,
            center: [0, 20]
          }}
          style={{
            width: '100%',
            height: '400px'
          }}
        >
          <ZoomableGroup zoom={1} center={[0, 20]}>
            <Geographies geography={geoUrl}>
              {({ geographies }: { geographies: any[] }) =>
                geographies.map((geo: any) => {
                  const countryName = geo.properties.name; // Use lowercase 'name' property
                  const data = countryData[countryName];
                  
                  return (
                    <Geography
                      key={geo.rsmKey}
                      geography={geo}
                      onMouseEnter={() => handleCountryHover(countryName)}
                      style={{
                        default: {
                          fill: getCountryColor(countryName),
                          stroke: getCountryStroke(countryName),
                          strokeWidth: 0.5,
                          outline: 'none',
                        },
                        hover: {
                          fill: data ? '#1e40af' : '#e5e7eb',
                          stroke: '#1e40af',
                          strokeWidth: 1,
                          outline: 'none',
                        },
                        pressed: {
                          fill: data ? '#1e40af' : '#e5e7eb',
                          stroke: '#1e40af',
                          strokeWidth: 1,
                          outline: 'none',
                        },
                      }}
                    />
                  );
                })
              }
            </Geographies>
          </ZoomableGroup>
        </ComposableMap>
        
        {/* Legend */}
        <div className="absolute bottom-4 left-4 bg-white bg-opacity-90 rounded-lg p-3 shadow-md">
          <div className="text-xs font-semibold text-gray-700 mb-2">Feedback Volume</div>
          <div className="flex items-center space-x-3">
            <div className="flex items-center space-x-1">
              <div className="w-4 h-3 bg-gray-200 rounded-sm"></div>
              <span className="text-xs text-gray-600">No data</span>
            </div>
            <div className="flex items-center space-x-1">
              <div className="w-4 h-3 bg-blue-200 rounded-sm"></div>
              <span className="text-xs text-gray-600">Low</span>
            </div>
            <div className="flex items-center space-x-1">
              <div className="w-4 h-3 bg-blue-400 rounded-sm"></div>
              <span className="text-xs text-gray-600">Medium</span>
            </div>
            <div className="flex items-center space-x-1">
              <div className="w-4 h-3 bg-blue-600 rounded-sm"></div>
              <span className="text-xs text-gray-600">High</span>
            </div>
          </div>
        </div>
        
        {/* Tooltip for hover effect could be added here */}
        {totalVotes === 0 && (
          <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-75 rounded-lg">
            <div className="text-center">
              <div className="text-4xl mb-2">🌍</div>
              <p className="text-gray-500">No geographic data yet</p>
              <p className="text-sm text-gray-400">Map will populate as feedback is received</p>
            </div>
          </div>
        )}
      </div>
      
      {totalVotes > 0 && (
        <div className="mt-3 pt-3 border-t border-gray-200">
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <div className="text-lg font-bold text-blue-600">{totalVotes.toLocaleString()}</div>
              <div className="text-xs text-gray-500">Total Votes</div>
            </div>
            <div>
              <div className="text-lg font-bold text-green-600">{countriesWithData}</div>
              <div className="text-xs text-gray-500">Countries</div>
            </div>
            <div>
              <div className="text-lg font-bold text-purple-600">
                {Math.round(totalVotes / Math.max(countriesWithData, 1))}
              </div>
              <div className="text-xs text-gray-500">Avg per Country</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

WorldMapChart.displayName = 'WorldMapChart'; 